/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo, useRef} from 'react';
import Graphic from '@arcgis/core/Graphic';
import {cloneDeep} from 'lodash';
import isNil from 'lodash.isnil';
import {error} from '@ion/core/src/logger/logger';
import {ALIGNED_ROUTES_CONFIG} from '@ion/core/src/services/Here';
import {createCSV} from '@ion/core/src/services/HereUtils';
import {CVSAssetType, GenericObject, HVSRecord, TripTypeAttribute} from '@ion/core/src/types/entities';
import {isCurrentTheme} from '../../theme/useThemeContext';
import {AssetsMap, ColorModeEnum, HistoryReportTypeEnum} from '../../types/types';
import {
  BACKGROUND_NODE_LAYER,
  BACKGROUND_PATH_LAYER,
  BACKGROUND_WORLD_LAYER,
  END_EVENT_NODE_LAYER,
  HISTORY_FIELDS,
  HISTORY_TRIP_SERVICE_FIELDS,
  IN_TRIP_NODE_LAYER,
  IN_TRIP_PATH_LAYER,
  mapViewGoToOptions,
  SymbolTypes
} from '../constants';
import {
  applyFilterToLayers,
  createAssetsMap,
  drawNodes,
  drawPath,
  getFeatureFilter,
  getLayerById,
  isEventType,
  reorderLayer
} from '../helpers/MapViewUtils';
import {EXTENT_FACTOR} from '../layers/AssetLayer';
import {isHistoryEnabled} from '../layers/LayerChooser';
import {createMarkers, MarkerMode} from '../markers/AssetMarkers';
import {useZoomTo} from './useZoomTo';

type AssetHistoryProviderProps = {
  map: __esri.Map;
  mapView: __esri.MapView;
  zoom: number;
  mapPannedRef: React.MutableRefObject<boolean>;
  featureFilter: GenericObject;
  isSelectionEnabled: boolean;
  onLayerDrawn: Function;
};

/**
 * useAssetHistoryMapController
 *
 * Centralized orchestration hook for rendering and managing Asset History
 * visualization on the ArcGIS map.
 *
 * This hook acts as the coordination layer between raw asset history data
 * and map presentation. It encapsulates all logic required to transform,
 * render, and interact with asset history across multiple map layers.
 *
 * Core responsibilities:
 * - Layer lifecycle management (creation, ordering, visibility, opacity)
 * - Transformation of asset history into map-ready graphics (nodes & paths)
 * - Route alignment (snap-to-road) and enrichment of route data
 * - Dynamic filtering and highlighting based on user interactions
 * - Map navigation control (zooming, panning, grouped focus)
 * - Synchronization of layer state with selection and feature filters
 * - Safe event binding and error handling for ArcGIS layer events
 *
 * The hook exposes a set of utilities and callbacks used by the UI layer
 * to control how asset history is displayed and interacted with on the map,
 * while keeping rendering logic isolated from data-fetching concerns.
 *
 * Notes:
 * - Designed to work alongside data-fetching hooks (e.g. useAssetHistoryQuery)
 * - Optimized to avoid unnecessary map updates and re-renders
 * - Handles both raw and route-aligned asset history scenarios
 *
 * @returns A collection of handlers, transformers, and layer references
 *          used to control asset history visualization on the map.
 */
const useAssetHistoryController = ({
  map,
  mapView,
  zoom,
  mapPannedRef,
  onLayerDrawn,
  featureFilter,
  isSelectionEnabled
}: AssetHistoryProviderProps) => {
  const inTripNodeViewLayerRef     = useRef(null);
  const backgroundNodeViewLayerRef = useRef(null);
  const endEventNodeViewLayerRef   = useRef(null);
  const layersDrawnRef             = useRef({
    IN_TRIP_NODE_LAYER    : false,
    IN_TRIP_PATH_LAYER    : false,
    BACKGROUND_NODE_LAYER : false,
    BACKGROUND_PATH_LAYER : false,
    END_EVENT_NODE_LAYER  : false
  });
  const zoomTo = useZoomTo();

  /**
   * Applies edits (add/update/delete) to the given FeatureLayer and optionally
   * executes a callback once the edits are successfully committed.
   *
   * @param featureLayer - The ArcGIS FeatureLayer to which edits will be applied
   * @param edits - Edits payload containing adds, updates, and/or deletes
   * @param callback - Optional function invoked after successful applyEdits completion
   */
  const applyEditsToLayer = useCallback((featureLayer: __esri.FeatureLayer, edits: any, callback?: Function) => {
    featureLayer?.applyEdits(edits)
      .then(() => {
        callback?.();
      });
  }, []);

  /**
   * Computes opacity values for background layers based on selection state.
   *
   * When selection is disabled, background layers are partially visible to provide
   * context (nodes at 40%, paths at 10%). When selection is enabled, both layers
   * are fully hidden (opacity = 0) to emphasize selectable elements.
   *
   * Memoized to recalculate only when the selection state changes.
   *
   * @returns Object containing opacity values for background node and path layers.
   */
  const opacityLayer = useMemo(() => {
    const isDisabled = !isSelectionEnabled;
    return {
      backgroundNodeLayer : isDisabled ? 0.4 : 0,
      backgroundPathLayer : isDisabled ? 0.1 : 0
    };
  }, [isSelectionEnabled]);
  
  /**
   * Resets the rendering order of all feature-related layers on the map.
   *
   * This ensures a consistent visual stacking by explicitly assigning z-index
   * positions to each layer. Foreground layers (e.g., event and trip nodes)
   * are placed above path and background layers, while the world background
   * mask remains at the lowest level.
   *
   * Layer order (top → bottom):
   *  - END_EVENT_NODE_LAYER
   *  - IN_TRIP_NODE_LAYER
   *  - IN_TRIP_PATH_LAYER
   *  - BACKGROUND_NODE_LAYER
   *  - BACKGROUND_PATH_LAYER
   *  - BACKGROUND_WORLD_LAYER
   *
   * Memoized to re-run only when the map instance changes.
   */
  const resetFeaturesLayer = useCallback(() => {
    reorderLayer(map, END_EVENT_NODE_LAYER, 5);
    reorderLayer(map, IN_TRIP_NODE_LAYER, 4);
    reorderLayer(map, IN_TRIP_PATH_LAYER, 3);
    reorderLayer(map, BACKGROUND_NODE_LAYER, 2);
    reorderLayer(map, BACKGROUND_PATH_LAYER, 1);
    reorderLayer(map, BACKGROUND_WORLD_LAYER, 0);
  }, [map]);

  /**
   * Handles the creation of the end-event node layer view.
   *
   * This callback is triggered when the END_EVENT_NODE_LAYER finishes rendering.
   * It stores the layer view reference, applies the current feature filter,
   * and updates the internal tracking state to indicate the layer has been drawn.
   *
   * Once updated, it checks whether all required layers have completed rendering.
   *
   * @param layerView - The rendered layer view instance for end-event nodes.
   */
  const checkLayersDrawnComplete = useCallback(() => {
    if (
      layersDrawnRef.current.IN_TRIP_NODE_LAYER &&
      layersDrawnRef.current.IN_TRIP_PATH_LAYER &&
      layersDrawnRef.current.BACKGROUND_NODE_LAYER &&
      layersDrawnRef.current.BACKGROUND_PATH_LAYER
    ) {
      setTimeout(onLayerDrawn, 2000);
    }
  }, [layersDrawnRef, onLayerDrawn]);

  /**
   * Callback executed when the END_EVENT_NODE_LAYER layer view is created.
   *
   * Assigns the layer view to a ref, applies the active feature filter,
   * and marks the layer as rendered in the tracking object.
   * After updating the state, it verifies whether all layers
   * have finished drawing.
   *
   * @param layerView - The LayerView instance associated with the end-event node layer.
   */
  const onEndEventNodeDrawn = useCallback(({layerView}) => {
    endEventNodeViewLayerRef.current        = layerView;
    endEventNodeViewLayerRef.current.filter = featureFilter;
    layersDrawnRef.current = {
      ...layersDrawnRef.current,
      END_EVENT_NODE_LAYER : true
    };
    checkLayersDrawnComplete();
  }, [layersDrawnRef, checkLayersDrawnComplete]);

  /**
   * Callback executed when the BACKGROUND_PATH_LAYER layer view is created.
   *
   * Marks the background path layer as rendered in the internal tracking state
   * and triggers a check to determine whether all layers have finished drawing.
   *
   * @param event - Layer view creation event (unused).
   */
  const onBackgroundPathDrawn = useCallback(event => {
    layersDrawnRef.current = {
      ...layersDrawnRef.current,
      BACKGROUND_PATH_LAYER : true
    };
    checkLayersDrawnComplete();
  }, [layersDrawnRef, checkLayersDrawnComplete]);

  /**
   * Callback executed when the BACKGROUND_NODE_LAYER layer view is created.
   *
   * Stores the layer view reference, applies the current feature filter,
   * and updates the tracking state to mark the background node layer as rendered.
   * Afterward, it checks whether all layers have completed drawing.
   *
   * @param layerView - The LayerView instance for the background node layer.
   */
  const onBackgroundNodeDrawn = useCallback(({layerView}) => {
    backgroundNodeViewLayerRef.current        = layerView;
    backgroundNodeViewLayerRef.current.filter = featureFilter;
    layersDrawnRef.current = {
      ...layersDrawnRef.current,
      BACKGROUND_NODE_LAYER : true
    };
    checkLayersDrawnComplete();
  }, [layersDrawnRef, checkLayersDrawnComplete, featureFilter]);

  /**
   * Callback executed when the IN_TRIP_PATH_LAYER layer view is created.
   *
   * Marks the in-trip path layer as rendered in the internal tracking state
   * and triggers a check to determine whether all layers have finished drawing.
   *
   * @param event - Layer view creation event (unused).
   */
  const onInTripPathDrawn = useCallback(event => {
    layersDrawnRef.current = {
      ...layersDrawnRef.current,
      IN_TRIP_PATH_LAYER : true
    };
    checkLayersDrawnComplete();
  }, [layersDrawnRef, checkLayersDrawnComplete]);

  /**
   * Callback executed when the IN_TRIP_NODE_LAYER layer view is created.
   *
   * Stores the layer view reference, applies the current feature filter,
   * and updates the tracking state to mark the in-trip node layer as rendered.
   * Afterward, it checks whether all layers have completed drawing.
   *
   * @param layerView - The LayerView instance for the in-trip node layer.
   */
  const onInTripNodeDrawn = useCallback(({layerView}) => {
    inTripNodeViewLayerRef.current        = layerView;
    inTripNodeViewLayerRef.current.filter = featureFilter;
    layersDrawnRef.current = {
      ...layersDrawnRef.current,
      IN_TRIP_NODE_LAYER : true
    };
    checkLayersDrawnComplete();
  }, [layersDrawnRef, checkLayersDrawnComplete, featureFilter]);

  /**
   * Enriches asset data with point geometry coordinates.
   *
   * Transforms each asset by adding a `geometry` property in Point format,
   * using the asset's longitude and latitude values. This prepares the data
   * for mapping/visualization layers that require spatial geometry.
   *
   * @param assets - Collection of asset objects containing latitude and longitude.
   *
   * @returns A new array of assets with geometry included.
   */
  const addGeometry = assets => {
    return assets?.map((item) => {
      return {
        ...item,
        geometry : {
          type        : 'Point',
          coordinates : [item.longitude, item.latitude]
        }
      };
    });
  };

  /**
   * Creates marker graphics for a collection of assets.
   *
   * If assets are provided, this function delegates to `createMarkers`
   * to generate detailed markers using the specified graphic class
   * and styling options. If no assets are present, it returns the
   * original input unchanged.
   *
   * @param assets - Collection of asset data used to generate markers.
   *
   * @returns Array of marker graphics or the original assets if empty.
   */
  const createMarker = useCallback((assets: CVSAssetType[]) => {
    if (assets.length) {
      return createMarkers({
        assets,
        Graphic,
        mode  : MarkerMode.detailed,
        color : ColorModeEnum.dark
      });
    }
    return assets;
  }, [createMarkers]);

  /**
   * Performs route matching for asset history data using cached aligned routes.
   *
   * This function triggers the route alignment process and then attempts to
   * replace each asset's raw data with its corresponding matched route result
   * (if available) from the provided cache.
   *
   * For each asset group:
   * - A CSV payload is generated based on configured headers.
   * - The payload is used as a key to look up precomputed route matching results.
   * - If a match is found, the asset data is replaced with the aligned route.
   *
   * Errors during processing are caught and logged to prevent breaking execution.
   *
   * @param assetsMap - Map of asset data grouped by identifier.
   * @param routeHistory - Cache containing previously computed aligned routes.
   */
  const getRouteMatching = useCallback(async(assetsMap: HVSRecord[], routeHistory: any) => {
    try {
      for (const key in assetsMap) {
        const {ALIGNED_ROUTES_CSV_HEADERS} = ALIGNED_ROUTES_CONFIG;
        const assets               = assetsMap[key];
        const requestBody          = createCSV(assets, ALIGNED_ROUTES_CSV_HEADERS) as any;
        const routeHistoryEnriched = enrichRouteHistory(assets, routeHistory, requestBody);
        const result               = routeHistoryEnriched[requestBody];
        if (result) {
          assetsMap[key] = result;
        }
      }
    } catch (err) {
      error(err);
    };
  }, []);

  /**
   * Updates map visibility flags for assets based on a reference object.
   *
   * Creates a deep clone of the provided assets map and synchronizes the
   * `mapVisibility` property for each asset (or node) using the corresponding
   * values from the reference structure.
   *
   * Supports both flat asset arrays and nested structures with `nodes`.
   *
   * @param assetsMap - Original map of asset data grouped by key.
   * @param ref - Reference object containing visibility states to apply.
   *
   * @returns A new assets map with updated visibility values.
   */
  const updateMapVisibility = useCallback((assetsMap: HVSRecord[], ref: any) => {
    const assetsMapClone = cloneDeep(assetsMap);
    for (const key in ref) {
      const item = ref[key];
      if (!Array.isArray(item)) continue;

      const target = assetsMapClone[key] as any;
      if (!target) continue;

      for (let i = 0; i < item.length; i++) {
        const mapVisibility = item[i]?.mapVisibility;
        if (target.nodes && Array.isArray(target.nodes)) {
          if (!target.nodes[i]) continue; // 🔑 prevent crash
          target.nodes[i].mapVisibility = mapVisibility;
        } else if (Array.isArray(target)) {
          if (!target[i]) continue; // 🔑 prevent crash
          target[i].mapVisibility = mapVisibility;
        }
      }
    }

    return assetsMapClone;
  }, []);

  /**
   * Safely binds an event handler to a FeatureLayer, catching any errors during execution.
   *
   * This utility ensures that the provided handler is executed asynchronously and
   * any exceptions are logged without breaking the application flow.
   *
   * @param layer - The FeatureLayer instance to bind the event to (can be null).
   * @param event - Name of the event to listen for (e.g., 'layerview-create').
   * @param handler - Asynchronous callback function to execute when the event occurs.
   */
  const safeBind = useCallback((layer: __esri.FeatureLayer | null, event: string, handler: Function) => {
    if (layer) {
      layer.on(event, async(e) => {
        try {
          await handler(e);
        } catch (err) {
          error(err);
        }
      });
    }
  }, []);
  
  /**
   * Adjusts the map view to center on a given geometry with optional zoom.
   *
   * This function calculates the extent of the provided geometry and navigates
   * the map view to that area. If the map is already centered on the extent,
   * it avoids unnecessary panning. It also supports centering on a specific
   * coordinate pair (longitude/latitude) if provided.
   *
   * @param geometry - The geometry (Point, Polygon, etc.) to zoom to.
   * @param eventGroupAttr - Optional object containing longitude/latitude for centering.
   */
  const moveMapView = useCallback((geometry: __esri.Geometry, eventGroupAttr?: GenericObject) => {
    try {
      const extent = zoomTo(geometry as any, EXTENT_FACTOR);
      if (!mapView?.extent?.contains(extent) || !mapPannedRef.current) {
        const {longitude, latitude} = eventGroupAttr || {};
        let goToOptions;
        if (longitude && latitude) {
          mapPannedRef.current = true;
          goToOptions = {zoom, center: [longitude, latitude]};
        } else {
          mapPannedRef.current = false;
          goToOptions = {extent};
        }
        mapView.goTo(goToOptions, mapViewGoToOptions);
      }
    } catch (err) {
      error(err);
    }
  }, [mapView, zoom, mapPannedRef]);

  /**
   * Enriches the `nodes` inside the routesHistory object by filling in missing properties
   * from the history array, matching records by OBJECTID.
   *
   * - Preserves the exact structure of `routesHistory` (including its dynamic top-level key)
   * - Iterates over `routesHistory[key].nodes` as the source of truth
   * - For each node:
   *    - Finds the corresponding node in `history` using OBJECTID
   *    - Copies only missing properties (undefined or null) from history into the node
   *    - Does NOT overwrite existing values in routesHistory
   * - Returns a new object with the same shape as `routesHistory`, with enriched nodes
   */
  const enrichRouteHistory = (history: HVSRecord[], routesHistory: GenericObject, key: string) => {
    const routeData = routesHistory[key] || {};
    const nodes     = routeData.nodes || [];
    const routes    = routeData.routes || [];

    const historyMap = Object.fromEntries(
      history.map(item => [item.OBJECTID, item])
    );

    return {
      ...routesHistory,
      [key] : {
        ...routeData,
        nodes : nodes.map(node => {
          const firstNode = historyMap[node.OBJECTID];
          if (firstNode) {
            const mergedNode = {...node};
            for (const prop in firstNode) {
              if (mergedNode[prop] === undefined || mergedNode[prop] === null) {
                mergedNode[prop] = firstNode[prop];
              }
            }
            return mergedNode;
          }
          return node;
        }),
        routes : routes.map(route => {
          const historyItem = historyMap[route.OBJECTID];
          if (historyItem) {
            return {
              ...route,
              tripId    : historyItem?.tripId ?? null,
              tripType  : historyItem?.tripType ?? null,
              tripGroup : historyItem.tripGroup
            };
          }
          return route;
        })
      }
    };
  };

  type GenerateAssetMapProps = {
    assets: HVSRecord[];
    isAssetSnapToRoadEnabled: boolean;
    routesHistory: GenericObject;
    boundary: any[];
    isTripServicesEnabled?: boolean;
    onlyAssetMap?: boolean;
  };

  /**
   * Generates the asset map along with its corresponding graphics for rendering.
   *
   * - Creates a deep-cloned asset map from the provided assets to avoid mutating the source data.
   * - Keeps a reference copy of the original map to later determine visibility changes.
   * - Optionally applies route matching (snap-to-road) to enrich asset routes when enabled.
   * - Updates map visibility by comparing the modified map against the original reference.
   * - Transforms asset data into:
   *    • Node graphics (events/points)
   *    • Path graphics (routes/lines)
   * - Extracts specific node graphics that represent start and end of trip events.
   *
   * @param assets - List of assets used to build the map structure.
   * @param boundary - Geographic boundary used when drawing paths.
   * @param routesHistory - Historical route data used for alignment/matching.
   * @param isAssetSnapToRoadEnabled - Flag indicating whether snap-to-road logic should be applied.
   * @param onlyAssetMap - Flag to return only AssetMap object.
   *
   * @returns An object containing:
   *  - assetsMap: The final processed asset map with visibility updates.
   *  - pathGraphic: Array of generated path (polyline) graphics.
   *  - endEventNodes: Node graphics representing start/end of trip events.
   */
  const generateAssetMap = useCallback(({
    assets,
    boundary,
    routesHistory,
    isAssetSnapToRoadEnabled,
    onlyAssetMap = false,
    isTripServicesEnabled = false
  }: GenerateAssetMapProps) => {
    const assetsMap    = cloneDeep(createAssetsMap(assets, null, true));
    const assetsMapRef = cloneDeep(assetsMap);

    if (isAssetSnapToRoadEnabled && !boundary?.length && Object.keys(routesHistory)?.length) {
      getRouteMatching(assetsMap as any, routesHistory);
    }
    
    const filteredAssetsMap    = !isTripServicesEnabled ? assetsMap : removeItemsByProperty(assetsMap);
    const filteredAssetsMapRef = !isTripServicesEnabled ? assetsMapRef : removeItemsByProperty(assetsMapRef);
    const assetsNodesMap       = updateMapVisibility(filteredAssetsMap as any, filteredAssetsMapRef) as any;
    const assetsPathMap        = updateMapVisibility(assetsMap as any, assetsMapRef) as any;
    
    if (!onlyAssetMap) {
      const nodeGraphic = Object.values(assetsNodesMap).map(asset => {
        const nodes = (asset as any).nodes || asset;
        return drawNodes(nodes, Graphic, !isTripServicesEnabled ? HISTORY_FIELDS : HISTORY_TRIP_SERVICE_FIELDS);
      }).flat();
      
      const pathGraphic = Object.values(assetsPathMap).map(asset => {
        const routes = (asset as any).routes || asset;
        return drawPath(routes, Graphic, boundary);
      }).flat();
    
      const endEventNodes = nodeGraphic.filter((data): boolean => {
        const isEndOfTripEvent   = isEventType(SymbolTypes.endOfTripEvent, data);
        const isStartOfTripEvent = isEventType(SymbolTypes.startOfTripEvent, data);
        return isEndOfTripEvent || isStartOfTripEvent;
      });

      return {
        assetsMap : assetsNodesMap,
        nodeGraphic,
        pathGraphic,
        endEventNodes
      };
    }
    return {assetsMap: assetsNodesMap};
  }, [HISTORY_FIELDS, HISTORY_TRIP_SERVICE_FIELDS, SymbolTypes]);

  /**
   * Removes items that match the specified property value from each asset.
   *
   * Supports two asset data structures:
   * - Assets containing `nodes` and `routes`, filtering both collections.
   * - Assets represented as a flat array, filtering the array directly.
   *
   * @param data The asset map to filter.
   * @param property The property to evaluate. Defaults to `tripType`.
   * @param propertyValue The property value to remove. Defaults to `TripTypeAttribute.Private`.
   * @returns A new asset map with matching items removed while preserving the original structure of each asset.
   */
  const removeItemsByProperty = (
    data: AssetsMap = {},
    property: string = 'tripType',
    propertyValue: TripTypeAttribute = TripTypeAttribute.Private
  ): AssetsMap => {
    if (!data || Object.keys(data).length === 0) return data;

    return Object.fromEntries(
      Object.entries(data).map(([assetId, assetData]) => {
        if (assetData?.nodes && assetData?.routes) {
          const nodes = assetData?.nodes?.filter(
            node => node?.[property] !== propertyValue
          );

          const routes = assetData?.routes?.filter(
            route => route?.[property] !== propertyValue
          );

          return [
            assetId,
            {
              ...assetData,
              nodes,
              routes
            }
          ];
        }
        return [
          assetId,
          assetData.filter(
            item => item?.[property] !== propertyValue
          )
        ];
      })
    );
  };

  type UpdateClickedGroupProps = {
    hoveredEvent: HVSRecord | null,
    map: __esri.Map,
    view: __esri.MapView
    isTripServicesEnabled?: boolean
  };

  /**
   * Updates map layer filtering based on the currently hovered event.
   *
   * - Extracts key attributes (id, tripGroup, eventTime, and event type) from the hovered event.
   * - Retrieves relevant map layers (node, path, and end-event node layers).
   * - Builds a dynamic filter expression based on the event context:
   *    • Non-trip events → filters by eventTime and id.
   *    • Trip events → prioritizes tripGroup and id when available.
   * - Applies filtering to node-related layers (including end-event nodes when present),
   *   using a feature filter for refined control.
   * - Applies filtering to the path layer separately, optionally hiding paths for non-trip events.
   *
   * @param hoveredEvent - The event currently hovered by the user (or null if none).
   * @param map - The ArcGIS map instance containing the layers.
   * @param mapView - The MapView used to apply visual filtering.
   * @param isTripServicesEnabled - Determines whether Trip Services are enabled.
   */
  const updateClickedGroup = useCallback(async({
    map,
    view,
    hoveredEvent,
    isTripServicesEnabled = false
  }: UpdateClickedGroupProps) => {
    const {
      id = null,
      tripGroup = null,
      isNonTripEvent = false,
      eventTime
    } = hoveredEvent || {};
  
    const nodeLayer    = getLayerById((map as any), IN_TRIP_NODE_LAYER);
    const pathLayer    = getLayerById((map as any), IN_TRIP_PATH_LAYER);
    const endNodeLayer = getLayerById((map as any), END_EVENT_NODE_LAYER);
    const layerList    = [nodeLayer];

    endNodeLayer && layerList.push(endNodeLayer);

    const tripGroupValue = !isTripServicesEnabled ? tripGroup : `'${tripGroup}'`;
  
    let filterOption = '';
    if (isNonTripEvent) {
      filterOption = `eventTime=${eventTime} AND id=${id}`;
    } else if (tripGroup && id) {
      filterOption = `tripGroup=${tripGroupValue} AND id=${id}`;
    } else if (tripGroup) {
      filterOption = `tripGroup=${tripGroupValue}`;
    } else if (id) {
      filterOption = `id=${id}`;
    }
  
    applyFilterToLayers({
      view,
      layers       : layerList,
      isPathHidden : isNonTripEvent,
      filterOption : getFeatureFilter(filterOption, featureFilter)
    });
  
    applyFilterToLayers({
      view,
      layers       : [pathLayer],
      isPathHidden : isNonTripEvent,
      filterOption
    });
  }, [featureFilter]);

  type UpdateGroupedViewProps = {
    assets: HVSRecord[];
    hoveredEvent: HVSRecord | null;
    skipMapZooming?: boolean;
    setZoomInitialized: Function;
  };

  /**
   * Updates the map view based on grouped asset data and the currently hovered event.
   *
   * - Builds a set of coordinates (geometry) from the provided assets.
   * - If a hovered event exists:
   *    • Filters assets by tripGroup when available.
   *    • Otherwise, filters by asset id.
   * - If no hovered event is provided, includes all asset coordinates.
   * - Optionally moves/zooms the map view to fit the resulting geometry.
   * - Marks zoom initialization as complete.
   *
   * @param assets - List of asset nodes containing geographic coordinates.
   * @param hoveredEvent - The currently hovered event used to filter assets (optional).
   * @param setZoomInitialized - Setter to indicate that the initial zoom has been applied.
   * @param skipMapZooming - Flag to skip map view movement when true.
   */
  const onUpdateGroupedView = useCallback(({
    assets,
    hoveredEvent,
    setZoomInitialized,
    skipMapZooming = false
  }: UpdateGroupedViewProps) => {
    let geometry: (string | number | undefined)[][] = [];
    if (hoveredEvent) {
      const {
        id,
        tripGroup = ''
      } = hoveredEvent;
      geometry = assets
        ?.filter(node => {
          return tripGroup ? node?.tripGroup === tripGroup : node?.id === Number(id);
        })
        .map(({longitude, latitude}) => [longitude, latitude]) || [];
    } else {
      geometry = assets
        ?.map(({longitude, latitude}) => [longitude, latitude]) || [];
    }
    !skipMapZooming && moveMapView(geometry);
    setZoomInitialized(true);
  }, []);

  /**
   * Determines whether history should be visible based on the provided layers.
   *
   * - If layers are provided, delegates the visibility check to `isHistoryEnabled`.
   * - If no layers are provided, defaults to `true` to ensure history remains visible.
   *
   * @param layers - Collection of map layers to evaluate.
   * @returns Boolean indicating whether history is visible.
   */
  const isHistoryVisible = useCallback(layers => {
    return layers ? isHistoryEnabled(layers) : true;
  }, []);
  
  /**
   * Determines whether the current report context is an Asset History report.
   *
   * - Returns true when no report is set (defaults to Asset History behavior).
   * - Returns true when the current report type is `Asset`.
   * - Returns false for any other report type.
   *
   * @param reportRef - Ref object containing the current report type.
   * @returns Boolean indicating if the report is an Asset History report.
   */
  const isHistoryReport = useCallback(reportRef => {
    return !reportRef.current || reportRef.current === HistoryReportTypeEnum.Asset;
  }, [HistoryReportTypeEnum]);

  /**
   * Retrieves the most recent (last) entry from the `assetsRoutesHistory` object.
   *
   * - Extracts all keys from the input object.
   * - Returns the value associated with the last key in the sorted list.
   * - Defaults to `null` if the input object is empty.
   *
   * @param assetsRoutesHistory - Object mapping time-based keys to asset/route history data.
   * @returns The latest asset and route history data or null if none exist.
   */
  const getLatestAssetsRoutesHistory = useCallback(assetsRoutesHistory => {
    const keys = Object.keys(assetsRoutesHistory);
    if (keys.length) {
      const lastKey = keys[keys.length - 1];
      return assetsRoutesHistory[lastKey];
    }
    return null;
  }, []);

  type insertAssetsRoutesIntoLayerProps = {
    assetsRoutesHistory: GenericObject,
    returnAllData?: boolean,
    isFirstRender?: boolean,
    callback: Function
  };

  /**
   * Inserts asset and route history data into the map layers.
   *
   * Behavior:
   * - If `isFirstRender` is true and there is more than one entry in `assetsRoutesHistory`,
   *   OR if `returnAllData` is true:
   *    • Aggregates and processes ALL asset and route history entries.
   *
   * - Otherwise:
   *    • Extracts and processes ONLY the latest asset and route history entry.
   *
   * @param assetsRoutesHistory - Object mapping time-based keys to asset/route history data.
   * @param returnAllData - When true, forces processing of the full history regardless of render state.
   * @param isFirstRender - Indicates whether this is the initial render cycle.
   * @param callback - Function executed with the resolved asset and route history data.
   */
  const insertAssetsRoutesIntoLayer = useCallback(({
    returnAllData = false,
    isFirstRender = false,
    assetsRoutesHistory,
    callback
  }: insertAssetsRoutesIntoLayerProps) => {
    const keys = Object.keys(assetsRoutesHistory);
    if ((isFirstRender && keys?.length > 1) || returnAllData) {
      const assetsHistory = getAllAssetsHistory(assetsRoutesHistory);
      const routesHistory = getAllRoutesHistory(assetsRoutesHistory);
      callback(assetsHistory, routesHistory);
    } else {
      const {assetsHistory, routesHistory} = getLatestAssetsRoutesHistory(assetsRoutesHistory);
      callback(assetsHistory, routesHistory);
    }
  }, []);

  /**
   * Aggregates all asset history data from the provided `assetsRoutesHistory` object.
   *
   * - Iterates through each entry in `assetsRoutesHistory`.
   * - Collects and flattens all `assetsHistory` entries into a single array.
   * - Returns the combined list of all historical asset data.
   *
   * @param assetsRoutesHistory - Object mapping time-based keys to asset/route history data.
   * @returns Flattened array of all asset history records.
   */
  const getAllAssetsHistory = useCallback(assetsRoutesHistory => {
    if (assetsRoutesHistory) {
      const allAssetsHistory = [];
      Object.keys(assetsRoutesHistory).map(key => {
        const {assetsHistory} = assetsRoutesHistory[key];
        // each event is added in turn - spreading an asset's whole list into
        // one push passes every event as an argument, which overflows the
        // call stack for an asset with a very long history ...
        for (const event of assetsHistory) {
          allAssetsHistory.push(event);
        }
      });
      return allAssetsHistory;
    }
    return assetsRoutesHistory;
  }, []);

  /**
   * Consolidates route history data from multiple assets into a single flattened
   * structure for efficient processing and rendering.
   *
   * The method processes a collection of asset route histories by extracting the
   * nodes and routes from each asset's route history and organizing them by their
   * inner keys. This transformation simplifies access to route data across
   * multiple assets.
   *
   * @param   assetsRoutesHistory An object containing route history data for
   *          multiple assets, where each asset contains a routesHistory object
   *          with nested route data including nodes and routes.
   * @returns A flattened object where keys are route identifiers and values
   *          contain the corresponding nodes and routes data.
   */
  const getAllRoutesHistory = useCallback(assetsRoutesHistory => {
    if (assetsRoutesHistory) {
      // in the current theme an entry's every route is kept. Pages merged on
      // a first load or a refresh land in one entry - keyed by its first
      // asset - carrying each asset's route under its own key, so keeping
      // only the first leaves the other assets' points unconnected. The keys
      // are each route's own request, so they never collide across entries.
      // The theme is read at call time, since this callback is created once ...
      const keepAllRoutes = isCurrentTheme();
      return Object.values(assetsRoutesHistory).reduce((acc, {routesHistory}) => {
        const routeKeys = Object?.keys(routesHistory);
        if (keepAllRoutes) {
          Object.assign(acc, routesHistory);
        } else if (routeKeys?.length) {
          const innerKey = routeKeys[0];
          const {nodes, routes} = routesHistory[innerKey];
          acc[innerKey] = {nodes, routes};
        }
        return acc;
      }, {});
    }
    return assetsRoutesHistory;
  }, []);

  return {
    safeBind,
    moveMapView,
    addGeometry,
    opacityLayer,
    createMarker,
    isHistoryReport,
    isHistoryVisible,
    generateAssetMap,
    applyEditsToLayer,
    onInTripPathDrawn,
    onInTripNodeDrawn,
    updateClickedGroup,
    resetFeaturesLayer,
    updateMapVisibility,
    getAllAssetsHistory,
    getAllRoutesHistory,
    onEndEventNodeDrawn,
    onUpdateGroupedView,
    onBackgroundPathDrawn,
    onBackgroundNodeDrawn,
    checkLayersDrawnComplete,
    inTripNodeViewLayerRef,
    backgroundNodeViewLayerRef,
    endEventNodeViewLayerRef,
    insertAssetsRoutesIntoLayer
  };
};

export default useAssetHistoryController;
